<script>
  import Calendar from "./Calendar.svelte";
  import Scheduler from "./Scheduler.svelte";
  import BookingForm from "./BookingForm.svelte";
  import BookingQuantity from "./BookingQuantity.svelte";
  import BookingSteps from "./BookingSteps.svelte";
  import BookingInfo from "./BookingInfo.svelte";
  import Confirmed from "./Confirmed.svelte";
  import Closer from "../base/Closer.svelte";
  import { createEventDispatcher } from "svelte";
  import { fade } from "svelte/transition";
  import { onMount } from "svelte";
  import { getBookingStatus } from "../../utils/sendwhatsapp_rewrite_chabi";
  import { getFormFromLocalStorage } from "../../utils/localStorage";

  const dispatch = createEventDispatcher();

  export let bookingConfig;
  export let data;
  export let person;
  export let noclose = false;

  const combinedCapacityMode =
    bookingConfig?.capacityEnabled === true &&
    bookingConfig?.showCalendarAndHour === true &&
    (noclose || data.landingDefault === true);

  let showBookingInfo = true;
  const getInitialQuantity = () => {
    if (bookingConfig?.capacityEnabled !== true) return undefined;

    const minimum = Number(bookingConfig.minimumCapacityPerBooking) || 1;
    const configuredMaximum = Number(bookingConfig.maximumCapacityPerBooking);
    const maximum = Number.isFinite(configuredMaximum) ? configuredMaximum : Infinity;
    const configuredDefault = Number(bookingConfig.defaultBookingQuantity);
    const defaultQuantity = Number.isInteger(configuredDefault) && configuredDefault > 0 ? configuredDefault : 3;
    return Math.min(Math.max(defaultQuantity, minimum), maximum);
  };

  let showCalendar = true;
  let showBookingQuantity = false;
  let showScheduler = false;
  let showBookingForm = false;
  let showConfirmed = false;
  let showFailed = false;
  let dateID = "";
  let dateHeading = "";
  let isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  let dayStore = [];
  let bookingTime;
  let bookingQuantity = getInitialQuantity();
  let bookingArrivalTime;
  let preselectedBookingConsumed = false;
  let quantityReturnStep;
  let bookingObj = {};
  let bookingFirstName = "";
  $: showCalendarAndHour =
    bookingConfig?.showCalendarAndHour === true &&
    (noclose || data.landingDefault === true);
  $: showWideScheduler = showCalendarAndHour && showCalendar && showScheduler;
  $: isEmbeddedWideBooking = noclose && showCalendarAndHour;
  $: bookingWidth = isEmbeddedWideBooking || showWideScheduler ? 640 : 800;
  $: contactFirst = data.orderBookingForm || bookingConfig.orderBookingForm;
  $: currentStep = showConfirmed || showFailed ? "confirmation" : showBookingForm ? "contact" : "booking";

  onMount(() => {
    if (data.saveDataInWidget) {
      try {
        const savedForm = getFormFromLocalStorage(data.id);
        const firstNameField = savedForm?.find(
          (item) => item.type === "text" && item.subtype === "firstname" && typeof item.val === "string"
        );
        bookingFirstName = firstNameField?.val?.trim() || "";
      } catch (_) {
        bookingFirstName = "";
      }
    }

    if ((data.orderBookingForm || bookingConfig.orderBookingForm) && !showBookingQuantity) {
      showCalendar = false;
      showBookingForm = true;
      showBookingInfo = false;
    }
  });

  const handleQuantitySubmit = (event) => {
    bookingQuantity = event.detail;

    if (combinedCapacityMode) {
      dayStore = [];
      bookingTime = undefined;
      bookingArrivalTime = undefined;
      dateID = "";
      dateHeading = "";
      showCalendar = true;
      showScheduler = true;
      showBookingInfo = false;
      return;
    }

    showBookingQuantity = false;
    showBookingInfo = false;

    if (quantityReturnStep === "calendar") {
      showCalendar = true;
    } else if (quantityReturnStep === "form" || data.orderBookingForm || bookingConfig.orderBookingForm) {
      showBookingForm = true;
    } else {
      showCalendar = true;
    }
    quantityReturnStep = undefined;
  };

  const handleChangeQuantity = () => {
    quantityReturnStep = showBookingForm ? "form" : "calendar";
    showCalendar = false;
    showScheduler = false;
    showBookingForm = false;
    showBookingQuantity = true;
    showBookingInfo = false;
    dayStore = [];
    bookingTime = undefined;
    bookingArrivalTime = undefined;
    dateID = "";
    dateHeading = "";
  };

  const handleDatePick = (value) => {
    showBookingInfo = false;
    showCalendar = showCalendarAndHour;
    showScheduler = true;
    dateID = value.detail.dateID;
    dayStore = value.detail.dayStore;
    dateHeading = dateID ? makeDateHeading() : "";
  };

  const handleTimePick = async (event) => {
    const selectedIndex = typeof event.detail === "object" ? event.detail.index : event.detail;
    bookingTime = dayStore[selectedIndex];
    bookingArrivalTime = typeof event.detail === "object" ? event.detail.arrivalTime : undefined;
    preselectedBookingConsumed = true;
    showScheduler = false;
    showCalendar = false;
    await continueAfterTimePick();
  };

  const continueAfterTimePick = async () => {
    if (data.orderBookingForm || bookingConfig.orderBookingForm) {
      const res = await getBookingStatus(
        bookingObj?.booking?.confId,
        bookingTime?.time?.substr(0, 25),
        bookingTime?.time?.substr(26),
        bookingQuantity,
        bookingArrivalTime
      );
      if (res.data?.status) {
        showConfirmed = true;
        dispatch("submit", {
          data: bookingObj.data,
          url: bookingObj?.url,
          booking: {
            confId: bookingObj?.booking?.confId,
            startTime: bookingTime?.time?.substr(0, 25),
            endTime: bookingTime?.time?.substr(26),
            timezone: bookingObj?.booking?.timezone,
            ...(bookingQuantity !== undefined ? { quantity: bookingQuantity } : {}),
            ...(bookingArrivalTime !== undefined ? { arrivalTime: bookingArrivalTime } : {}),
          },
          ...(bookingObj?.termsAccepted !== undefined ? { termsAccepted: bookingObj.termsAccepted } : {}),
        });
      } else {
        showFailed = true;
      }
    } else {
      showBookingForm = true;
    }
  };

  const handleSubmit = async (event) => {
    bookingObj = {
      data: event.detail.data,
      url: event.detail?.url,
      booking: event.detail.booking,
      ...(event.detail?.termsAccepted !== undefined ? { termsAccepted: event.detail.termsAccepted } : {}),
    };
    if (data.orderBookingForm || bookingConfig.orderBookingForm) {
      showCalendar = true;
    } else {
      const res = await getBookingStatus(
        bookingObj?.booking?.confId,
        bookingObj?.booking?.startTime,
        bookingObj?.booking?.endTime,
        bookingQuantity,
        bookingArrivalTime
      );
      if (res.data?.status) {
        dispatch("submit", bookingObj);
        if (event.detail?.isClosedConversation) {
          return;
        }
        showConfirmed = true;
      } else {
        showFailed = true;
      }
    }
    showBookingInfo = false;
    showBookingForm = false;
  };

  const handleBack = () => {
    if (showBookingForm) {
      if (data.orderBookingForm || bookingConfig.orderBookingForm) {
        dispatch("close");
      } else {
        showBookingForm = false;
        showScheduler = true;
        showCalendar = showCalendarAndHour;
      }
    } else if (showScheduler) {
      showBookingInfo = true;
      showScheduler = false;
      showCalendar = true;
    }
  };

  const makeDateHeading = () => {
    let dateAsHeading = dateID.replace(/_/g, " ");
    let date = new Date(`${dateAsHeading}`);
    return (dateHeading = date.toLocaleString(navigator.language ?? "en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }));
  };

  const clickOutside = (node) => {
    const handleClick = (event) => {
      if (node && !node.contains(event.target) && !event.defaultPrevented) {
        node.dispatchEvent(new CustomEvent("click_outside", node));
      }
    };
    document.addEventListener("click", handleClick, true);
    return {
      destroy() {
        document.removeEventListener("click", handleClick, true);
      },
    };
  };
</script>

<svelte:head>
  <link rel="stylesheet" href="https://unpkg.com/mono-icons@1.0.5/iconfont/icons.css" />
</svelte:head>
<div
  class:isMobile
  class:chatwith-booking-with-times={showWideScheduler}
  class:chatwith-booking-combined-capacity={combinedCapacityMode && (showCalendar || showScheduler)}
  class="chatwith-booking"
  style="width: {bookingWidth}px{isEmbeddedWideBooking ? ' !important' : ''}"
  use:clickOutside
  on:click_outside={() => {}}
  transition:fade={{ duration: 125 }}
>
  <BookingSteps {currentStep} {contactFirst} />
  {#if !noclose && (showConfirmed || showFailed || showBookingInfo)}
    <Closer on:close={() => dispatch("close")} />
  {/if}
  {#if showBookingInfo && !showCalendar}
    <BookingInfo {showBookingForm} firstName={bookingFirstName} on:back={handleBack} />
  {/if}
  {#if combinedCapacityMode && (showCalendar || showScheduler)}
    <div class="chatwith-booking-combined-quantity">
      <BookingQuantity
        on:submit={handleQuantitySubmit}
        minimum={bookingConfig.minimumCapacityPerBooking || 1}
        maximum={bookingConfig.maximumCapacityPerBooking}
        value={bookingQuantity || bookingConfig.minimumCapacityPerBooking || 1}
        label={bookingConfig.attendeesLabel}
      />
    </div>
  {/if}
  <div class={showConfirmed || showFailed ? "chatwith-booking-confirm-wrapper" : "chatwith-booking-content"}>
    {#if showCalendar}
      {#if bookingFirstName}
        <div class="chatwith-booking-calendar-greeting">
          <BookingInfo {showBookingForm} firstName={bookingFirstName} on:back={handleBack} />
        </div>
      {/if}
      {#key bookingQuantity}
        <Calendar
          on:click={handleDatePick}
          on:changeQuantity={handleChangeQuantity}
          {bookingConfig}
          {data}
          quantity={bookingQuantity}
          availabilityEnabled={true}
          showQuantitySummary={!combinedCapacityMode}
          autoShowTimes={showCalendarAndHour}
          initialDate={data.preselectedBookingDate}
        />
      {/key}
    {:else if showBookingQuantity}
      <BookingQuantity
        on:submit={handleQuantitySubmit}
        minimum={bookingConfig.minimumCapacityPerBooking || 1}
        maximum={bookingConfig.maximumCapacityPerBooking}
        value={bookingQuantity || bookingConfig.minimumCapacityPerBooking || 1}
        label={bookingConfig.attendeesLabel}
      />
    {:else if showBookingForm}
      <BookingForm
        on:submit={handleSubmit}
        on:back={handleBack}
        {data}
        {person}
        {bookingTime}
        {bookingConfig}
        quantity={bookingQuantity}
        arrivalTime={bookingArrivalTime}
        {isMobile}
      />
    {/if}
    {#if showConfirmed || showFailed}
      <Confirmed {bookingTime} {dateHeading} {person} {data} failed={showFailed} />
    {/if}
  </div>
  {#if showScheduler}
    <Scheduler
      {dateHeading}
      on:back={handleBack}
      on:confirm={handleTimePick}
      on:changeQuantity={handleChangeQuantity}
      {data}
      {dayStore}
      {bookingConfig}
      quantity={bookingQuantity}
      showQuantitySummary={!combinedCapacityMode}
      initialTime={preselectedBookingConsumed ? null : data.preselectedBookingTime}
    />
  {/if}
</div>
